import {FC, useState} from 'react';
//mui
import {Box, Table, TableCell, TableHead, TableRow, Typography, useMediaQuery} from '@mui/material';
//formik
import {Field, useFormikContext} from 'formik';
//constants
import {
  BIDDER_ID,
  BODY1,
  EMAIL,
  FIELD,
  FIELD_LABEL,
  FIRST_NAME,
  HASH,
  LAST_NAME,
  NONE,
  PHONE_NUMBER,
  PLACEHOLDER,
  SPECIAL_MEAL,
  SUBTITLE1,
  TICKETS,
  VIP,
  EMAIL_TICKET,
  mealsSelectOptions,
  defaultPageSize,
  MARK_AS_PAID,
} from '@/utils/constants';
//components
import Input from '@/shared/components/Inputs/Input';
import MultipleSelect from '@/shared/components/MultipleSelect';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import Checkbox from '@/shared/components/Checkbox';
//enums
import {ITeamRole, MENU_OPTION} from '@/utils/enums';
//helpers
import {tableTicketMenuContent} from './helpers';
//types
import {IOrganizationTableTicketsResponse, IOrganizationTablesTicket} from '@/utils/types';
//styles
import {tableFieldsStyle} from './TableFields.styles';
import Modal from '@/shared/components/Modal';
import {attendeesTableStyle} from '@/components/Organization/Attendees/AttendeesTable/AttendeesTable.styles';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getAttendee, getAttendeeSelector} from '@/store/organization-attendees';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import AttendeeDetails from '@/components/Organization/Attendees/AttendeeDetails';
import {getCurrentEventSelector} from '@/store/organization-event';
import Button from '@/shared/components/Button';
import {CommunicationService} from '@/utils/services/organization-communication.services';
import {getTicketsList, updateTicketStatus} from '@/store/organization-tables';
import {getUserSelector} from '@/store/user';
import {useApiInstance} from '@/contexts/ApiInstanceContext';
import { getTeamSelector } from '@/store/organization';
import { tablesAndTicketsTableStyle } from '../../../TablesAndTicketsTable/TablesAndTicketsTable.styles';

const TableFields: FC = () => {
  const {
    setFieldValue,
    values: {tableInfo, tickets, isPaid},
  } = useFormikContext<IOrganizationTableTicketsResponse>();

  const user = useAppSelector(getUserSelector);
  const dispatch = useAppDispatch();
  const attendee = useAppSelector(getAttendeeSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const team = useAppSelector(getTeamSelector);

  const isStandartMember = !!team && team.role === ITeamRole.STANDART;

  const isLaptop = useMediaQuery('(min-width:1200px)');

  const {apiInstance} = useApiInstance();

  const [isShowAttendeeDetailsModal, setIsShowAttendeeDetailsModal] = useState<boolean>(false);
  const [isProfilePage, setIsProfilePage] = useState<boolean>(false);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState<boolean>(false);

  const handleProfileTabStyles = (isProfilePage: boolean) => setIsProfilePage(isProfilePage);
  const handleAccessModalChange = () => setIsAccessModalOpen(!isAccessModalOpen);

  const handleChangeAttendeeDetailsModal = async (attendeeId?: number) => {
    setIsShowAttendeeDetailsModal(!isShowAttendeeDetailsModal);

    if (!isShowAttendeeDetailsModal && attendeeId) {
      try {
        await dispatch(getAttendee(attendeeId)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }

    if (isShowAttendeeDetailsModal) {
      //Todo
    }
  };

  const getTicketsListData = async () => {
    try {
      const reqBody = {
        eventId: currentEvent!.id,
        page: 1,
        pageSize: isLaptop ? defaultPageSize : 10000,
      };

      await dispatch(getTicketsList({reqBody, apiInstance})).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdateStatus = async ({id, isPaid}: {id: number; isPaid: boolean}) => {
    if (!currentEvent) return;
	if (isStandartMember) {
	  handleAccessModalChange();
	  return;
	}

    try {
      await dispatch(updateTicketStatus({tableId: id, isPaid})).unwrap();
      await getTicketsListData();
      toast.success('Payment status updated successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSendTicket = async (attendeeId?: number) => {
    if (!attendeeId) return;
    try {
      await CommunicationService.emailAttendeeTicket(attendeeId);
      toast.success('Ticket sent successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDeleteAttendee = async (attendeeId: number, isUnknown: boolean, attendeeIndex: number) => {
    if (!currentEvent || isUnknown) return;
    try {
      setFieldValue(`${TICKETS}[${attendeeIndex}].${FIELD.firstName}`, '');
      setFieldValue(`${TICKETS}[${attendeeIndex}].${FIELD.lastName}`, '');
      setFieldValue(`${TICKETS}[${attendeeIndex}].${FIELD.email}`, '');
      setFieldValue(`${TICKETS}[${attendeeIndex}].${FIELD.phoneNumber}`, '');
      setFieldValue(`${TICKETS}[${attendeeIndex}].${FIELD.meals}`, []);
      setFieldValue(`${TICKETS}[${attendeeIndex}].${FIELD.isVIP}`, false);
      setFieldValue(`${TICKETS}[${attendeeIndex}].${FIELD.isUnknown}`, true);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  const handleClickMenuOption =
    (option: string, ticketId: number, attendeeId: any, isUnknown: boolean, attendeeIndex: number) => () => {
      switch (option) {
        case MENU_OPTION.REMOVE:
          handleDeleteAttendee(attendeeId, isUnknown, attendeeIndex);
          break;

        case MENU_OPTION.GO_TO_PROFILE:
          handleChangeAttendeeDetailsModal(attendeeId);
          break;

        case MENU_OPTION.EMAIL_TICKET:
          handleSendTicket(attendeeId);
          break;

        case MENU_OPTION.MARK_AS_PAID:
          handleUpdateStatus({id: ticketId, isPaid: true});

          break;

        default:
          break;
      }
    };

  return (
    <Box sx={tableFieldsStyle.wrapper}>
      <Typography variant={SUBTITLE1} sx={tableFieldsStyle.title}>
        {tableInfo.tier}:
      </Typography>

      <Table sx={tableFieldsStyle.table}>
        <TableHead>
          <TableRow sx={tableFieldsStyle.tableHeader}>
            <TableCell>
              {BIDDER_ID}
              {HASH}
            </TableCell>
            <TableCell>{FIRST_NAME}</TableCell>
            <TableCell>{LAST_NAME}</TableCell>
            <TableCell>{EMAIL}</TableCell>
            <TableCell>{PHONE_NUMBER}</TableCell>
            <TableCell>{SPECIAL_MEAL}</TableCell>
            <TableCell>{VIP}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
      </Table>

      {tickets.map((ticket: IOrganizationTablesTicket, attendeeIndex: number) => (
        <Box key={attendeeIndex} sx={tableFieldsStyle.tableFields}>
          <Box>
            <Typography variant={BODY1} sx={tableFieldsStyle.title}>
              {HASH}
              {ticket.id}
            </Typography>
          </Box>
          <Field
            id={`${TICKETS}[${attendeeIndex}].${FIELD.firstName}`}
            name={`${TICKETS}[${attendeeIndex}].${FIELD.firstName}`}
            placeholder={PLACEHOLDER.firstName}
            label={FIELD_LABEL.firstName}
            value={ticket.firstName}
            component={Input}
            isArrayData
          />
          <Field
            id={`${TICKETS}[${attendeeIndex}].${FIELD.lastName}`}
            name={`${TICKETS}[${attendeeIndex}].${FIELD.lastName}`}
            placeholder={PLACEHOLDER.lastName}
            label={FIELD_LABEL.lastName}
            value={ticket.lastName}
            component={Input}
            isArrayData
          />
          <Field
            id={`${TICKETS}[${attendeeIndex}].${FIELD.email}`}
            name={`${TICKETS}[${attendeeIndex}].${FIELD.email}`}
            placeholder={PLACEHOLDER.emailEnter}
            label={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <span>{FIELD_LABEL.email}</span>
                <Button
                  title={EMAIL_TICKET}
                  size="small"
                  variant="text"
                  onClick={() => handleSendTicket(ticket.id)}
                  sx={{fontSize: '12px', height: '20px'}}
                  disabled={!ticket.email || ticket.isUnknown || !currentEvent}
                />
              </Box>
            }
            value={ticket.email}
            component={Input}
            isArrayData
          />
          <Field
            id={`${TICKETS}[${attendeeIndex}].${FIELD.phoneNumber}`}
            name={`${TICKETS}[${attendeeIndex}].${FIELD.phoneNumber}`}
            placeholder={PLACEHOLDER.phoneNumberZero}
            label={FIELD_LABEL.phoneNumber}
            value={ticket.phoneNumber}
            component={Input}
            isArrayData
          />
          <Field
            id={`${TICKETS}[${attendeeIndex}].${FIELD.meals}`}
            name={`${TICKETS}[${attendeeIndex}].${FIELD.meals}`}
            value={ticket.meals}
            defaultValue={NONE}
            label={FIELD_LABEL.meals}
            component={MultipleSelect}
            options={mealsSelectOptions}
            isArrayData
          />

          <Field
            id={`${TICKETS}[${attendeeIndex}].${FIELD.isVIP}`}
            name={`${TICKETS}[${attendeeIndex}].${FIELD.isVIP}`}
            value={ticket.isVIP}
            component={Checkbox}
            isStarIcon
          />
          <ActionsMenu
            disableRipple
            content={
              <Box>
                {tableTicketMenuContent
                  .filter((item) => (user ? item !== MENU_OPTION.MARK_AS_PAID : true))
                  .map((item) => (
                    <Typography
                      key={item}
                      onClick={handleClickMenuOption(item, ticket.id, ticket.id, ticket.isUnknown, attendeeIndex)}
                      variant={BODY1}
                      sx={{
                        ...tableFieldsStyle.menuItem,
                        ...(item === MENU_OPTION.MARK_AS_PAID && isPaid && tableFieldsStyle.menuDisableItem),
                        ...(item === MENU_OPTION.EMAIL_TICKET &&
                          (!ticket.email || ticket.isUnknown || !currentEvent) &&
                          tableFieldsStyle.menuDisableItem),
                        ...(item === MENU_OPTION.REMOVE &&
                          (ticket.isUnknown ||
                            (!ticket.firstName &&
                              !ticket.lastName &&
                              !ticket.email &&
                              !ticket.phoneNumber &&
                              !ticket.meals.length)) &&
                          tableFieldsStyle.menuDisableItem),
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
              </Box>
            }
          />
        </Box>
      ))}

      <Modal
        onClose={handleChangeAttendeeDetailsModal}
        open={isShowAttendeeDetailsModal && !!attendee}
        sx={{...attendeesTableStyle.detailsModal, ...(isProfilePage && attendeesTableStyle.profilePage)}}
      >
        <AttendeeDetails
          onCloseModal={handleChangeAttendeeDetailsModal}
          handleProfileTabStyles={handleProfileTabStyles}
        />
      </Modal>
	    <Modal
        open={isAccessModalOpen}
        onClose={handleAccessModalChange}
        title="Access Denied"
        description="You do not have access to this feature"
        sx={tablesAndTicketsTableStyle.modalNotify}
      />
    </Box>
  );
};

export default TableFields;
